<template>
  <layout>
    <template v-slot:header>
      <global-navigation></global-navigation>
    </template>

    <main
      v-if="cartData"
      class="Cart"
    >
      <h1 class="wcag-hidden">Cart</h1>

      <div
        v-if="cartCount > 0"
        class="cart-wrapper"
      >
        <div :class="{ 'shopify-cart__list': true, cartIsUpdating }">
          <div class="cart--header cart-grid">
            <div class="cart--item">Item</div>
            <div class="cart--details-desktop">Details</div>
            <div class="cart--quantity">Quantity</div>
            <div class="cart--price">Price</div>
          </div>
          <div class="cart--body">
            <div
              class="cart--row cart-grid"
              v-for="item in lineItems"
              :key="item.node.id"
            >
              <div class="cart--item">
                <div class="cart--item-inner">
                  <figure>
                    <img
                      v-if="item.node.merchandise.image.url"
                      :src="item.node.merchandise.image.url"
                    />
                  </figure>
                  <div>
                    <div class="uppercase">{{ item.node.merchandise.product.title }}</div>
                    <div class="cart--details-mobile">
                      <div
                        v-if="hasVariant(item)"
                        class="variant uppercase"
                      >
                        {{ item.node.merchandise.title }}
                      </div>
                      <button
                        @click.prevent="removeItem(item)"
                        class="mp-mini-button with-underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="cart--details-desktop">
                <div
                  v-if="hasVariant(item)"
                  class="variant uppercase"
                >
                  {{ item.node.merchandise.title }}
                </div>
                <button
                  @click.prevent="removeItem(item)"
                  class="mp-mini-button with-underline"
                >
                  Remove
                </button>
              </div>

              <div class="cart--quantity">
                <div class="mp-number">
                  <div>
                    {{ formatMoney(item.node.cost.amountPerQuantity.amount) }} &times;
                    <input
                      type="number"
                      :value="item.node.quantity"
                      @change="updateItemQuantity(item, Math.min(parseInt($event.target.value), 5))"
                    />
                  </div>
                  <div>
                    <button @click="updateItemQuantity(item, decrementItemQuantity(item))">-</button>
                    <button
                      @click="updateItemQuantity(item, incrementItemQuantity(item))"
                      :disabled="item.node.quantity >= 5"
                    >
                      +
                    </button>
                  </div>
                  <div
                    v-if="item.node.quantity >= 5"
                    class="mini-type color-red"
                  >
                    5 item maximum
                  </div>
                </div>
              </div>
              <div class="cart--price">{{ formatMoney(item.node.cost.subtotalAmount.amount) }}</div>
            </div>
          </div>
          <form
            class="cart--footer cart-grid"
            @submit="handleSubmit"
          >
            <div class="continue-shopping"><vue-link to="/shop">&larr; Continue Shopping</vue-link></div>
            <div class="subtotal-label">Subtotal</div>
            <div class="subtotal-price">{{ formatMoney(cartData.cost.subtotalAmount.amount) }}</div>
            <div class="checkout">
              <div class="agreement">
                <input
                  type="checkbox"
                  id="terms"
                  name="terms"
                  required="required"
                  class="mp-chk"
                />
                <label for="terms"
                  >I agree to the <vue-link to="/shop/terms">Terms of Service</vue-link> and acknowledge the
                  <vue-link to="/shop/privacy">Privacy Policy</vue-link></label
                >
              </div>
              <button
                type="submit"
                :data-src="cartData.checkoutUrl"
                class="mp-button"
              >
                Proceed to checkout
              </button>
            </div>
          </form>
        </div>
      </div>
      <div
        v-else
        class="empty-cart links-dashed"
      >
        Your cart is empty, <vue-link to="/shop">continue shopping</vue-link>.
      </div>
    </main>

    <template v-slot:footer>
      <global-footer :border="hasBorder"></global-footer>
    </template>
  </layout>
</template>

<script>
import { getTotalCartItems, updateLineItems, formatMoney } from '@/lib/shopify-utils'
import PageMixin from '@/mixins/PageMixin'

export default {
  name: 'Page',
  mixins: [PageMixin],
  data() {
    return {
      cartIsUpdating: false,
    }
  },
  computed: {
    console: () => console,
    hasBorder() {
      return this.hasPage && (!!this.page.videofile || !!(this.page.featuredimage && this.page.featuredimage.large))
    },
    collectionId() {
      const site = this.$store.state.content['/*'] || {}
      return site.collectionId
    },
    cartData() {
      return this.$store.state.cartData
    },
    cartCount() {
      if (this.$store.state.cartData) {
        return getTotalCartItems(this.$store.state.cartData)
      }

      return 0
    },
    lineItems() {
      return this.$store.state.cartData.lines.edges
    },
  },
  methods: {
    hasVariant(item) {
      return item.node.merchandise.title !== 'Default Title'
    },
    formatPrice(price) {
      return price.replace('.00', '').replace('.0', '')
    },
    formatMoney(price) {
      return formatMoney(price)
    },
    linePrice(price, count) {
      let parsedPrice = parseFloat(price)
      let parsedCount = parseInt(count) || 1
      return String(parsedPrice * parsedCount)
    },
    incrementItemQuantity(item) {
      let quantity = item.node.quantity
      return Math.min(quantity + 1, 5)
    },
    decrementItemQuantity(item) {
      let quantity = item.node.quantity
      return quantity - 1
    },
    handleSubmit(e) {
      e.preventDefault()
      let submitButton = e.target.querySelector('button[data-src]')
      let checkoutURL = submitButton.dataset.src
      window.open(checkoutURL)
    },
    async removeItem(item) {
      this.cartIsUpdating = true

      const lineItemsToUpdate = [
        {
          id: item.node.id,
          quantity: 0,
        },
      ]

      const response = await updateLineItems({
        cartId: this.$store.state.cartData.id,
        lines: lineItemsToUpdate,
      })

      if (response.errors) {
        console.error('Error updating line item:', response.errors)
        return
      }

      this.$store.dispatch('setCartData', response.data.cartLinesUpdate.cart)

      this.cartIsUpdating = false
    },
    async updateItemQuantity(item, quantity) {
      this.cartIsUpdating = true

      const lineItemsToUpdate = [
        {
          id: item.node.id,
          quantity: quantity,
        },
      ]

      const response = await updateLineItems({
        cartId: this.$store.state.cartData.id,
        lines: lineItemsToUpdate,
      })

      if (response.errors) {
        console.error('Error updating line item:', response.errors)
        return
      }

      this.$store.dispatch('setCartData', response.data.cartLinesUpdate.cart)

      this.cartIsUpdating = false
    },
  },
}
</script>

<style scoped>
.cart-wrapper {
  padding-left: 2rem;
  padding-right: 2rem;
  max-width: 1300px;
  margin-left: auto;
  margin-right: auto;
}

.empty-cart {
  padding-left: 2rem;
  padding-right: 2rem;
  text-align: center;
  font: var(--nord);
}

.cartIsUpdating {
  cursor: wait !important;
  user-select: none;
}

.cartIsUpdating * {
  cursor: wait !important;
}

.shopify-cart__list {
  font: var(--nord);
  width: 100%;
}

.cart-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.cart--item {
  grid-column: span 3 / span 3;
}

.cart--details-desktop {
  grid-column: span 3 / span 3;
}

.cart--quantity {
  grid-column: span 1 / span 1;
}

.cart--price {
  grid-column: span 1 / span 1;
  text-align: right;
}

.cart--header {
  font-size: 1.15rem;
  padding-bottom: 1rem;
}

.cart--row {
  border-top: 1px dashed #fff;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.cart--item-inner {
  display: flex;
}

.cart--details-mobile {
  display: none;
}

.cart--item-inner figure {
  position: relative;
  margin-right: 1rem;
  width: 120px;
}

.cart--item-inner figure::after {
  content: '';
  display: block;
  padding-top: 75%;
}

.cart--item-inner figure img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cart--footer {
  border-top: 1px dashed #fff;
  padding-top: 3rem;
}

.cart-grid.cart--footer {
  row-gap: 5rem;
}

.mp-number > * {
  display: inline-block;
  margin-bottom: 0.6rem;
}

.continue-shopping {
  grid-column: span 6 / span 6;
  font-size: 1.15rem;
}

.subtotal-label {
  grid-column: span 1 / span 1;
  text-transform: uppercase;
}

.subtotal-price {
  grid-column: span 1 / span 1;
  text-align: right;
}

.agreement {
  /*grid-column: 5 / span 2;
  align-self: center;*/
  /*justify-self: end;*/

  margin-bottom: 1.5rem;
}

@media (min-width: 1001px) {
  .agreement {
    font-size: 1.15rem;
  }
}

.agreement a {
  text-decoration: underline;
}

.checkout {
  grid-column: 7 / span 2;
  /*grid-column: span 2 / span 2;*/
}

.checkout .mp-button {
  text-transform: uppercase;
  width: 100%;
}

.variant {
  margin-bottom: 0.5rem;
}

.not-found {
  text-align: center;
  font: var(--nord);
  text-transform: uppercase;
}

.content {
  padding: 4rem 19% 6rem 19%;
}

@media (max-width: 1360px) {
  .content {
    padding: 4rem 20%;
  }
}

@media (max-width: 1000px) {
  .content {
    padding: 4rem 2rem;
  }
  .cart--details-desktop {
    display: none;
  }
  .cart--details-mobile {
    display: block;
  }
  .cart--item {
    grid-column: span 5 / span 5;
  }
  .cart--quantity {
    grid-column: span 2 / span 2;
  }
  .cart--item-inner figure {
    width: 75px;
  }
  .continue-shopping {
    grid-column: span 5 / span 5;
  }

  .subtotal-label {
    grid-column: span 2 / span 2;
  }

  .subtotal-price {
    grid-column: span 1 / span 1;
  }

  .checkout {
    grid-column: 1 / -1;
    text-align: center;
  }
}

@media (max-width: 350px) {
  .cart--item-inner figure {
    display: none;
  }
  .continue-shopping {
    grid-column: span 4 / span 4;
  }

  .subtotal-label {
    grid-column: span 3 / span 3;
  }
}
</style>
